/**
 * RedactionEditor - Canvas-based screenshot redaction tool.
 *
 * Renders a screenshot image with an absolutely-positioned canvas overlay. The user
 * draws black rectangles over sensitive regions by clicking and dragging. When
 * satisfied, they click "Use redacted" which composites the rectangles onto the image
 * via an off-screen canvas and returns the redacted Blob via the onComplete callback.
 *
 * Controls:
 *   - Undo: removes the last drawn rectangle.
 *   - Use redacted: composites and returns the redacted blob.
 *   - Cancel: returns without calling onComplete.
 *
 * @module components/RedactionEditor
 */

import React, { useRef, useState, useEffect, useCallback } from 'react';

/**
 * RedactionEditor component.
 *
 * @param {Object} props
 * @param {Blob} props.imageBlob - The screenshot blob to redact.
 * @param {function(Blob): void} props.onComplete - Called with the redacted PNG blob.
 * @param {function(): void} props.onCancel - Called when user cancels without redacting.
 * @returns {JSX.Element} The redaction editor UI.
 */
function RedactionEditor({ imageBlob, onComplete, onCancel }) {
  const imgRef = useRef(null);
  const canvasRef = useRef(null);
  const [rectangles, setRectangles] = useState([]);
  const [currentDrag, setCurrentDrag] = useState(null);
  const [imageObjectUrl] = useState(() => URL.createObjectURL(imageBlob));

  /**
   * Redraw the canvas: clear, then stroke all committed rectangles, then the current
   * in-progress drag rectangle (if any).
   *
   * @param {CanvasRenderingContext2D} ctx - Canvas 2D rendering context.
   * @param {Array<{x:number,y:number,w:number,h:number}>} rects - Committed rectangles.
   * @param {{x:number,y:number,w:number,h:number}|null} drag - In-progress drag rect.
   */
  function redraw(ctx, rects, drag) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = 'black';
    rects.forEach(({ x, y, w, h }) => ctx.fillRect(x, y, w, h));
    if (drag) {
      ctx.fillRect(drag.x, drag.y, drag.w, drag.h);
    }
  }

  // Sync canvas size to image when image loads.
  const handleImageLoad = useCallback(() => {
    const img = imgRef.current;
    const canvas = canvasRef.current;
    if (!img || !canvas) return;
    canvas.width = img.naturalWidth || img.offsetWidth;
    canvas.height = img.naturalHeight || img.offsetHeight;
  }, []);

  // Escape key to cancel.
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === 'Escape') onCancel();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  /**
   * Record the starting position of a drag on the canvas.
   *
   * @param {React.MouseEvent} e
   */
  function handleMouseDown(e) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const startX = (e.clientX - rect.left) * scaleX;
    const startY = (e.clientY - rect.top) * scaleY;
    setCurrentDrag({ startX, startY, x: startX, y: startY, w: 0, h: 0 });
  }

  /**
   * Update the current drag rectangle while the mouse moves.
   *
   * @param {React.MouseEvent} e
   */
  function handleMouseMove(e) {
    if (!currentDrag) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const curX = (e.clientX - rect.left) * scaleX;
    const curY = (e.clientY - rect.top) * scaleY;
    const drag = {
      ...currentDrag,
      x: Math.min(currentDrag.startX, curX),
      y: Math.min(currentDrag.startY, curY),
      w: Math.abs(curX - currentDrag.startX),
      h: Math.abs(curY - currentDrag.startY),
    };
    setCurrentDrag(drag);

    const ctx = canvas.getContext('2d');
    redraw(ctx, rectangles, drag);
  }

  /**
   * Commit the current drag rectangle to the rectangles list.
   */
  function handleMouseUp() {
    if (!currentDrag) return;
    const { x, y, w, h } = currentDrag;
    if (w > 2 && h > 2) {
      // Only commit if the rect has meaningful area.
      const updated = [...rectangles, { x, y, w, h }];
      setRectangles(updated);
      const ctx = canvasRef.current.getContext('2d');
      redraw(ctx, updated, null);
    }
    setCurrentDrag(null);
  }

  /**
   * Remove the last committed rectangle.
   */
  function handleUndo() {
    const updated = rectangles.slice(0, -1);
    setRectangles(updated);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    redraw(ctx, updated, null);
  }

  /**
   * Composite the rectangles onto the image using an off-screen canvas, then return the
   * redacted PNG blob via onComplete.
   */
  function handleUseRedacted() {
    const img = imgRef.current;
    if (!img) return;

    const offscreen = document.createElement('canvas');
    offscreen.width = img.naturalWidth || img.offsetWidth;
    offscreen.height = img.naturalHeight || img.offsetHeight;
    const ctx = offscreen.getContext('2d');
    ctx.drawImage(img, 0, 0);
    ctx.fillStyle = 'black';
    rectangles.forEach(({ x, y, w, h }) => ctx.fillRect(x, y, w, h));

    offscreen.toBlob((blob) => {
      onComplete(blob);
    }, 'image/png');
  }

  return (
    <div className="redaction-editor">
      <p className="redaction-editor__hint">
        Click and drag on the image to draw black redaction boxes over sensitive areas.
        Press <kbd>Esc</kbd> or click Cancel to skip redaction.
      </p>

      <div className="redaction-editor__canvas-wrap">
        <img
          ref={imgRef}
          src={imageObjectUrl}
          alt="Screenshot to redact"
          className="redaction-editor__image"
          onLoad={handleImageLoad}
          draggable={false}
        />
        <canvas
          ref={canvasRef}
          className="redaction-editor__canvas"
          role="img"
          aria-label="Screenshot redaction canvas — click and drag to draw redaction boxes"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        />
      </div>

      <div className="redaction-editor__controls">
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={handleUndo}
          disabled={rectangles.length === 0}
          aria-label="Undo last redaction box"
        >
          Undo
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={handleUseRedacted}
          aria-label="Apply redactions and use this image"
        >
          Use redacted
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={onCancel}
          aria-label="Cancel redaction and go back"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default RedactionEditor;
